<template>
  <div v-if="isCardPayment">
    <app-bar>
      <close-button @click="cancelPayment" />
    </app-bar>

    <!-- Added card list -->
    <card-list v-model:paymentMethodId="selectedPaymentId" />

    <!-- Card form -->
    <form @submit.prevent="handleCardPayment" class="style-payment-form">
      <div v-show="selectedPaymentId === 0">
        <stripe-payment-form
          :elements="elements"
          v-model:billingCountry="billingCountry"
          @completed="setPaymentFormStatus"
        />
      </div>
      <default-button title="Pay" class="mt-5" :disabled="!payButtonEnabled" />
    </form>

    <div v-if="errorMessage" class="style-error-message mt-4">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import CloseButton from "@/components/button/CloseButton";
import AppBar from "@/components/AppBar";

import { loadStripe } from "@stripe/stripe-js";
import {
  cancelOnlinePayment,
  paymentIntent,
  postPaymentSuccess,
} from "@/services/stripe";
import { mapMutations, mapGetters } from "vuex";
import { ticketStatusList } from "@/common/constant/TicketStatus";
import StripePaymentForm from "@/components/onlinePayment/StripePaymentForm";
import DefaultButton from "@/components/button/DefaultButton";
import CardList from "@/components/onlinePayment/CardList";
import { accountType, stripePaymentMethod } from "@/common/constant/Stripe";

export default {
  name: "BaseStripe",
  components: {
    CloseButton,
    AppBar,
    StripePaymentForm,
    DefaultButton,
    CardList,
  },
  props: {
    successRedirectURL: {
      type: String,
      required: true,
    },
    failedRedirectURL: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      // component
      saleTicketDetails: null,
      elements: null,
      billingCountry: null,
      payButtonEnabled: false,
      errorMessage: null,
      selectedPaymentId: 0,
      paymentFormCompleted: false,
      // stripe
      stripe: null,
      stripePublicKey: null,
      stripeAccount: null,
      stripePaymentIntent: null,
      stripeClientSecret: null,
    };
  },
  computed: {
    isCardPayment() {
      return this.paymentMethod.gateway.key2 === stripePaymentMethod.CARD;
    },
    ...mapGetters(["selectedBranch"]),
  },
  watch: {
    paymentFormCompleted(value) {
      this.payButtonEnabled = value;
    },
    selectedPaymentId(value) {
      if (value) {
        this.payButtonEnabled = true;
      } else {
        this.payButtonEnabled = this.paymentFormCompleted;
      }
    },
  },
  methods: {
    submit() {
      // You will be redirected to Stripe's secure checkout page
      this.$refs.checkoutRef.redirectToCheckout();
    },
    async cancelPayment() {
      this.showLoading(true);

      // Remove sessionStorage
      sessionStorage.removeItem("saleTicket");

      const payload = {
        payment_intent: this.stripePaymentIntent,
        stripeAccount: this.stripeAccount.id,
        token: this.saleTicketDetails.token,
        accountType: this.stripeAccount.type,
      };

      try {
        await cancelOnlinePayment(payload);
        this.$router.push(this.failedRedirectURL);
      } catch (error) {
        console.log(error);
      }
      this.showLoading(false);
    },
    async handlePayNowPayment() {
      const paymentMethodId = this.createPaymentMethod(
        stripePaymentMethod.PAYNOW
      );

      await this.stripe
        .confirmPayNowPayment(this.stripeClientSecret, {
          payment_method: paymentMethodId,
        })
        .then((response) => {
          if (response.paymentIntent.status === "succeeded") {
            this.showLoading(true);
            this.handlePaymentSuccess(response.paymentIntent.status);
          } else {
            this.cancelPayment();
          }
        });
    },
    async getPaymentIntent() {
      const payload = {
        roundedTotal: this.saleTicketDetails.roundedTotal,
        paymentMethodId: this.paymentMethod.id,
        branchId: this.selectedBranch.id,
      };

      try {
        const { data } = await paymentIntent(payload);

        // Save data
        this.stripePaymentIntent = data.id;
        this.stripeClientSecret = data.client_secret;
        this.stripePublicKey = data.publishable_key;
        this.stripeAccount = {
          id: data.account_id,
          type: data.account_type,
        };
      } catch (error) {
        console.log(error);
      }
    },
    async loadStripe() {
      try {
        // Check if it is stripe standard
        if (this.stripeAccount.type === accountType.STANDARD) {
          this.stripe = await loadStripe(this.stripePublicKey, {
            stripeAccount: this.stripeAccount.id,
          });
        } else {
          this.stripe = await loadStripe(this.stripePublicKey);
        }

        if (this.isCardPayment) {
          this.elements = this.stripe.elements();
        } else {
          this.handlePayNowPayment();
        }
      } catch (error) {
        console.log(error);
      }
    },
    async setupStripe() {
      this.showLoading(true);

      this.saleTicketDetails = JSON.parse(sessionStorage.getItem("saleTicket"));
      this.paymentMethod = JSON.parse(sessionStorage.getItem("selectedPaymentMethod"));

      // await Promise.all([this.getPaymentIntent(), this.loadStripe()]);
      await this.getPaymentIntent();
      await this.loadStripe();

      this.showLoading(false);
    },
    async handleCardPayment() {
      this.showLoading(true);

      try {
        let paymentId = this.selectedPaymentId;
        // If user using new card for payment
        if (paymentId === 0) {
          paymentId = await this.createPaymentMethod(stripePaymentMethod.CARD);
        }

        // Make Payment
        const response = await this.stripe.confirmCardPayment(
          this.stripeClientSecret,
          {
            payment_method: paymentId,
          }
        );

        // Check if payment success
        if (
          response.paymentIntent &&
          response.paymentIntent.status === "succeeded"
        ) {
          this.handlePaymentSuccess(response.paymentIntent.status);
          return;
        }

        // If error occur during confirm card payment
        if (response.error) {
          // this.errorMessage = response.error.message;
          this.cancelPayment();
        }
      } catch (error) {
        console.log(error);
        this.cancelPayment();
        this.showLoading(false);
        this.$router.push(this.failedRedirectURL);
      }
    },
    async createPaymentMethod(paymentType) {
      let response;

      // If Stripe card payment
      if (paymentType === stripePaymentMethod.CARD) {
        const cardNumberElement = this.elements.getElement("cardNumber");

        // Post payment methods
        response = await this.stripe.createPaymentMethod({
          type: stripePaymentMethod.CARD,
          card: cardNumberElement,
          billing_details: {
            address: {
              country: this.billingCountry,
            },
          },
        });
      } else {
        // If paynow payment
        response = await this.stripe.createPaymentMethod({
          type: stripePaymentMethod.PAYNOW,
        });
      }

      return response.paymentMethod.id;
    },
    async handlePaymentSuccess(paymentIntentStatus) {
      const payload = {
        token: this.saleTicketDetails.token,
        payment_intent: this.stripePaymentIntent,
        payment_intent_client_secret: this.stripeClientSecret,
        redirect_status: paymentIntentStatus,
        stripeAccount: this.stripeAccount.id,
        accountType: this.stripeAccount.type,
      };

      // Set sessionStorage
      sessionStorage.setItem("stripePaymentTicketId", this.saleTicketDetails.id);

      // Remove sessionStorage
      sessionStorage.removeItem("saleTicket");

      try {
        await postPaymentSuccess(payload);
        this.showLoading(false);

        // use on appointment only, not on cart
        const appointments = this.saleTicketDetails?.appointments || [];
        const status = appointments.length > 0 ? appointments.shift().status : ticketStatusList.CONFIRMED;
        sessionStorage.setItem("booking-status", status);

        this.$router.push(this.successRedirectURL);
      } catch (error) {
        this.showLoading(false);
        console.log(error);
      }
    },
    setPaymentFormStatus(event) {
      this.paymentFormCompleted = event;
    },
    ...mapMutations(["showLoading"]),
  },
  async created() {
    // retrieve previous page(checkout page) saleticket, payment method. And fetch payment intent from Stripe API
    this.setupStripe();
  },
};
</script>

<style lang="scss" scoped>
.style-payment-form {
  width: 90%;
  margin: auto;
}

.style-error-message {
  color: $error-color;
}
</style>
